import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSettings } from '../context/SettingsContext';
import type { ModelConfig } from '../services/settings.service';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

// Provider display names and colors
const PROVIDER_INFO: { [key: string]: { name: string; color: string } } = {
  'azure-openai': { name: 'Azure OpenAI', color: '#0078D4' },
  'anthropic': { name: 'Anthropic Claude', color: '#D97706' },
};

const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, onClose }) => {
  const {
    availableModels,
    groupedModels,
    currentModel,
    environment,
    isLoading,
    error,
    setSelectedModel,
    refreshCurrentModel,
    clearError,
  } = useSettings();

  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load current model when dialog opens
  useEffect(() => {
    if (open) {
      refreshCurrentModel();
      setSaveSuccess(false);
      clearError();
    }
  }, [open, refreshCurrentModel, clearError]);

  // Set selected model when current model loads
  useEffect(() => {
    if (currentModel) {
      setSelectedModelId(currentModel.model_id);
    }
  }, [currentModel]);

  const handleModelChange = (event: SelectChangeEvent<string>) => {
    setSelectedModelId(event.target.value);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedModelId) return;
    
    setIsSaving(true);
    setSaveSuccess(false);
    
    const success = await setSelectedModel(selectedModelId);
    
    setIsSaving(false);
    if (success) {
      setSaveSuccess(true);
      // Auto-close after short delay on success
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  const handleClose = () => {
    clearError();
    setSaveSuccess(false);
    onClose();
  };

  const getSelectedModelInfo = (): ModelConfig | undefined => {
    return availableModels.find((m) => m.model_id === selectedModelId);
  };

  const selectedInfo = getSelectedModelInfo();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1C1E22',
          color: '#BFC1C2',
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #2F3337',
        pb: 2
      }}>
        <Typography variant="h6" component="span" sx={{ color: '#F6FFE7' }}>
          Settings
        </Typography>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{ color: '#BFC1C2' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Environment Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#9CA3AF', mb: 1 }}>
            Environment
          </Typography>
          <Chip 
            label={environment.toUpperCase()} 
            size="small"
            sx={{ 
              bgcolor: environment === 'prod' ? '#DC2626' : environment === 'local' ? '#059669' : '#2563EB',
              color: '#fff',
              fontWeight: 600
            }}
          />
        </Box>

        <Divider sx={{ bgcolor: '#2F3337', my: 2 }} />

        {/* Model Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#9CA3AF', mb: 2 }}>
            LLM Model Selection
          </Typography>

          {isLoading && !currentModel ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: '#007CB0' }} />
            </Box>
          ) : (
            <FormControl fullWidth variant="outlined">
              <InputLabel 
                id="model-select-label"
                sx={{ 
                  color: '#9CA3AF',
                  '&.Mui-focused': { color: '#007CB0' }
                }}
              >
                Select Model
              </InputLabel>
              <Select
                labelId="model-select-label"
                value={selectedModelId}
                onChange={handleModelChange}
                label="Select Model"
                sx={{
                  color: '#F6FFE7',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#2F3337' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#007CB0' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#007CB0' },
                  '.MuiSvgIcon-root': { color: '#9CA3AF' }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#26292E',
                      color: '#BFC1C2',
                      maxHeight: 400,
                    }
                  }
                }}
              >
                {/* Group models by provider */}
                {Object.entries(groupedModels).map(([provider, models]) => [
                  <MenuItem 
                    key={`header-${provider}`} 
                    disabled 
                    sx={{ 
                      bgcolor: '#1C1E22', 
                      color: PROVIDER_INFO[provider]?.color || '#9CA3AF',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {PROVIDER_INFO[provider]?.name || provider}
                  </MenuItem>,
                  ...models.map((model: ModelConfig) => (
                    <MenuItem 
                      key={model.model_id} 
                      value={model.model_id}
                      sx={{
                        '&:hover': { bgcolor: '#3F4349' },
                        '&.Mui-selected': { bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' } }
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {model.display_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                          {model.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ]).flat()}
              </Select>
            </FormControl>
          )}

          {/* Selected Model Info */}
          {selectedInfo && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#26292E', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#F6FFE7' }}>
                  {selectedInfo.display_name}
                </Typography>
                <Chip 
                  label={PROVIDER_INFO[selectedInfo.provider]?.name || selectedInfo.provider}
                  size="small"
                  sx={{ 
                    bgcolor: PROVIDER_INFO[selectedInfo.provider]?.color || '#6B7280',
                    color: '#fff',
                    fontSize: '0.65rem',
                    height: 20
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                {selectedInfo.description}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mt: 0.5 }}>
                Region: {selectedInfo.region}
              </Typography>
              
              {/* Provider-specific warning for Anthropic */}
              {selectedInfo.provider === 'anthropic' && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 2,
                    bgcolor: '#1E3A5F',
                    color: '#93C5FD',
                    fontSize: '0.75rem',
                    '& .MuiAlert-icon': { color: '#93C5FD' }
                  }}
                >
                  Claude models don't have native JSON mode, so responses may take longer. For optimal performance, GPT models are recommended.
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 2,
              bgcolor: '#7F1D1D',
              color: '#FCA5A5',
              '& .MuiAlert-icon': { color: '#FCA5A5' }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Success Display */}
        {saveSuccess && (
          <Alert 
            severity="success" 
            sx={{ 
              mt: 2,
              bgcolor: '#14532D',
              color: '#86EFAC',
              '& .MuiAlert-icon': { color: '#86EFAC' }
            }}
          >
            Model selection saved successfully!
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #2F3337' }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            color: '#9CA3AF',
            '&:hover': { bgcolor: '#26292E' }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !selectedModelId || selectedModelId === currentModel?.model_id}
          sx={{ 
            bgcolor: '#007CB0',
            '&:hover': { bgcolor: '#005F87' },
            '&.Mui-disabled': { bgcolor: '#2F3337', color: '#6B7280' }
          }}
        >
          {isSaving ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
